/**
 * FL-132: 取引先デモデータ
 *
 * デモ会社（株式会社サンプル陸送）用の取引先マスタデータ
 * リモートSupabaseに登録済みのデータと同期
 */

import {
  BusinessPartner,
  BusinessPartnerType,
  BusinessPartnerCategory,
  BusinessPartnerRole,
  BusinessPartnerStatus,
  BusinessPartnerDataSource,
} from '../../types';
import { DEMO_COMPANY_ID } from '../../constants/demo';

/**
 * デモ用取引先データ（Supabaseに登録済み）
 */
export const DEMO_BUSINESS_PARTNERS: Partial<BusinessPartner>[] = [
  // ディーラー系
  {
    id: '11111111-1111-1111-1111-111111111111',
    companyId: DEMO_COMPANY_ID,
    name: '株式会社オートディーラーA',
    displayName: 'オートディーラーA',
    type: BusinessPartnerType.CORPORATE,
    category: BusinessPartnerCategory.DEALERSHIP,
    defaultAddress: '東京都港区六本木1-1-1',
    defaultPhone: '03-1111-1111',
    defaultContactPerson: '営業部',
    defaultEmail: 'dealer-a@demo.example.com',
    availableRoles: [BusinessPartnerRole.BILLING, BusinessPartnerRole.DELIVERY],
    status: BusinessPartnerStatus.ACTIVE,
    dataSource: BusinessPartnerDataSource.CUSTOM,
    isSystemPreset: false,
    notes: 'デモ用ディーラー（高級車取扱）',
  },
  {
    id: '22222222-2222-2222-2222-222222222222',
    companyId: DEMO_COMPANY_ID,
    name: '株式会社オートディーラーB',
    displayName: 'オートディーラーB',
    type: BusinessPartnerType.CORPORATE,
    category: BusinessPartnerCategory.DEALERSHIP,
    defaultAddress: '神奈川県横浜市西区みなとみらい2-2-2',
    defaultPhone: '045-222-2222',
    defaultContactPerson: '車両管理部',
    defaultEmail: 'dealer-b@demo.example.com',
    availableRoles: [BusinessPartnerRole.BILLING, BusinessPartnerRole.DELIVERY],
    status: BusinessPartnerStatus.ACTIVE,
    dataSource: BusinessPartnerDataSource.CUSTOM,
    isSystemPreset: false,
    notes: 'デモ用ディーラー（国産車専門）',
  },
  {
    id: '33333333-3333-3333-3333-333333333333',
    companyId: DEMO_COMPANY_ID,
    name: '株式会社オートディーラーC',
    displayName: 'オートディーラーC',
    type: BusinessPartnerType.CORPORATE,
    category: BusinessPartnerCategory.DEALERSHIP,
    defaultAddress: '千葉県千葉市中央区中央3-3-3',
    defaultPhone: '043-333-3333',
    defaultContactPerson: '中古車部',
    defaultEmail: 'dealer-c@demo.example.com',
    availableRoles: [BusinessPartnerRole.PICKUP],
    status: BusinessPartnerStatus.ACTIVE,
    dataSource: BusinessPartnerDataSource.CUSTOM,
    isSystemPreset: false,
    notes: 'デモ用ディーラー（中古車専門）',
  },

  // オークション会場
  {
    id: '44444444-4444-4444-4444-444444444444',
    companyId: DEMO_COMPANY_ID,
    name: '東京オートオークション',
    displayName: 'オークション会場',
    type: BusinessPartnerType.CORPORATE,
    category: BusinessPartnerCategory.AUCTION,
    defaultAddress: '東京都江東区有明4-4-4',
    defaultPhone: '03-4444-4444',
    defaultContactPerson: 'オークション事務局',
    defaultEmail: 'auction@demo.example.com',
    availableRoles: [
      BusinessPartnerRole.BILLING,
      BusinessPartnerRole.PICKUP,
      BusinessPartnerRole.DELIVERY,
    ],
    status: BusinessPartnerStatus.ACTIVE,
    dataSource: BusinessPartnerDataSource.CUSTOM,
    isSystemPreset: false,
    notes: 'デモ用オークション会場',
  },

  // 個人顧客
  {
    id: '55555555-5555-5555-5555-555555555555',
    companyId: DEMO_COMPANY_ID,
    name: '山田 太郎',
    displayName: '個人顧客A',
    type: BusinessPartnerType.INDIVIDUAL,
    category: BusinessPartnerCategory.OTHER,
    defaultAddress: '東京都世田谷区成城5-5-5',
    defaultPhone: '090-5555-5555',
    defaultContactPerson: '山田 太郎',
    defaultEmail: 'yamada@demo.example.com',
    availableRoles: [BusinessPartnerRole.PICKUP, BusinessPartnerRole.DELIVERY],
    status: BusinessPartnerStatus.ACTIVE,
    dataSource: BusinessPartnerDataSource.CUSTOM,
    isSystemPreset: false,
    notes: 'デモ用個人顧客A',
  },
  {
    id: '66666666-6666-6666-6666-666666666666',
    companyId: DEMO_COMPANY_ID,
    name: '鈴木 花子',
    displayName: '個人顧客B',
    type: BusinessPartnerType.INDIVIDUAL,
    category: BusinessPartnerCategory.OTHER,
    defaultAddress: '神奈川県鎌倉市雪ノ下6-6-6',
    defaultPhone: '090-6666-6666',
    defaultContactPerson: '鈴木 花子',
    defaultEmail: 'suzuki@demo.example.com',
    availableRoles: [BusinessPartnerRole.PICKUP, BusinessPartnerRole.DELIVERY],
    status: BusinessPartnerStatus.ACTIVE,
    dataSource: BusinessPartnerDataSource.CUSTOM,
    isSystemPreset: false,
    notes: 'デモ用個人顧客B',
  },
];

/**
 * 取引先IDマッピング（コード内での参照用）
 */
export const PARTNER_ID_MAP = {
  DEALERS: {
    A: '11111111-1111-1111-1111-111111111111',
    B: '22222222-2222-2222-2222-222222222222',
    C: '33333333-3333-3333-3333-333333333333',
  },
  AUCTION: '44444444-4444-4444-4444-444444444444',
  INDIVIDUALS: {
    A: '55555555-5555-5555-5555-555555555555',
    B: '66666666-6666-6666-6666-666666666666',
  },
} as const;

/**
 * 取引先統計情報
 */
export const PARTNER_STATS = {
  TOTAL: DEMO_BUSINESS_PARTNERS.length,
  BY_TYPE: {
    CORPORATE: DEMO_BUSINESS_PARTNERS.filter(p => p.type === 'corporate').length,
    INDIVIDUAL: DEMO_BUSINESS_PARTNERS.filter(p => p.type === 'individual').length,
  },
  BY_CATEGORY: {
    DEALERSHIP: DEMO_BUSINESS_PARTNERS.filter(p => p.category === 'DEALERSHIP').length,
    AUCTION: DEMO_BUSINESS_PARTNERS.filter(p => p.category === 'AUCTION').length,
    OTHER: DEMO_BUSINESS_PARTNERS.filter(p => p.category === 'OTHER').length,
  },
  BY_ROLE: {
    BILLING: DEMO_BUSINESS_PARTNERS.filter(p =>
      p.availableRoles?.includes(BusinessPartnerRole.BILLING)
    ).length,
    PICKUP: DEMO_BUSINESS_PARTNERS.filter(p =>
      p.availableRoles?.includes(BusinessPartnerRole.PICKUP)
    ).length,
    DELIVERY: DEMO_BUSINESS_PARTNERS.filter(p =>
      p.availableRoles?.includes(BusinessPartnerRole.DELIVERY)
    ).length,
  },
} as const;

export default DEMO_BUSINESS_PARTNERS;
